if (!customElements.get('cart-threshold-bar')) {
  class CartThresholdBar extends HTMLElement {
    #cartUpdateUnsubscriber = null;

    #getRoot() {
      return this.closest('.drawer__content') || this;
    }

    connectedCallback() {
      this.#bindElements();
      this.moneyFormat = window.FoxTheme?.settings?.moneyFormat || '${{amount}}';

      this.#updateFromSubtotal(Number(this.dataset.cartSubtotal) || 0, this.dataset.cartEmpty === 'true');

      document.addEventListener('cart:updated', this.#onCartUpdated);
      this.#cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        ({ cart }) => {
          if (!cart) return;
          this.#updateFromSubtotal(cart.items_subtotal_price || 0, cart.item_count === 0);
        }
      );
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.#onCartUpdated);
      this.#cartUpdateUnsubscriber?.();
    }

    #onCartUpdated = (event) => {
      const cart = event.detail?.cart;
      if (!cart) return;
      this.#updateFromSubtotal(cart.items_subtotal_price || 0, cart.item_count === 0);
    };

    #bindElements() {
      const root = this.#getRoot();

      this.banner = this.querySelector('[data-threshold-banner]');
      this.bannerIcon = this.querySelector('[data-threshold-banner-icon]');
      this.bannerText = this.querySelector('[data-threshold-banner-text]');
      this.trackWrap = root.querySelector('[data-threshold-track-wrap]');
      this.fill = root.querySelector('[data-threshold-fill]');
      this.milestones = [...root.querySelectorAll('[data-threshold-milestone]')];
      this.#sortMilestones();
    }

    #sortMilestones() {
      this.milestones.sort((a, b) => {
        return this.#getThresholdCents(a) - this.#getThresholdCents(b);
      });

      const list = this.#getRoot().querySelector('[data-threshold-milestones]');
      if (!list) return;

      this.milestones.forEach((milestone) => list.appendChild(milestone));
    }

    #getThresholdCents(milestone) {
      return Number(milestone.dataset.thresholdCents) || 0;
    }

    #getResolvedMilestones() {
      return this.milestones
        .map((milestone) => ({
          el: milestone,
          threshold: this.#getThresholdCents(milestone),
          type: milestone.dataset.milestoneType,
          label: milestone.dataset.label || '',
          messageRemaining: milestone.dataset.messageRemaining || '',
          messageAchieved: milestone.dataset.messageAchieved || '',
        }))
        .filter((milestone) => milestone.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold);
    }

    #updateFromSubtotal(subtotalCents, isEmpty) {
      this.#bindElements();

      if (this.trackWrap) {
        this.trackWrap.classList.toggle('hidden', isEmpty);
      }

      if (isEmpty) {
        this.#hideBanner();
        return;
      }

      const milestones = this.#getResolvedMilestones();
      if (milestones.length === 0 || this.dataset.hasThresholds === 'false') {
        this.#showStaticBanner();
        return;
      }

      const maxThreshold = milestones[milestones.length - 1].threshold;
      const progressPercent = Math.min((subtotalCents / maxThreshold) * 100, 100);

      if (this.fill) {
        this.fill.style.width = `${progressPercent}%`;
      }

      milestones.forEach((milestone) => {
        const achieved = subtotalCents >= milestone.threshold;
        const marker = milestone.el.querySelector('.cart-threshold-bar__milestone-marker');
        marker?.classList.toggle('cart-threshold-bar__milestone-marker--achieved', achieved);
      });

      const nextMilestone = milestones.find((milestone) => subtotalCents < milestone.threshold);

      if (nextMilestone) {
        const remaining = nextMilestone.threshold - subtotalCents;
        const html = this.#formatMessage(nextMilestone.messageRemaining, {
          amount: remaining,
          threshold: nextMilestone.threshold,
          reward: nextMilestone.label,
        });
        this.#showBanner(html, nextMilestone.el);
        return;
      }

      const lastMilestone = milestones[milestones.length - 1];
      const html = this.#formatMessage(lastMilestone.messageAchieved, {
        amount: 0,
        threshold: lastMilestone.threshold,
        reward: lastMilestone.label,
      });
      this.#showBanner(html, lastMilestone.el);
    }

    #showBanner(html, milestoneEl) {
      if (!this.banner || !html) {
        this.#hideBanner();
        return;
      }

      this.banner.hidden = false;

      if (this.bannerText) {
        this.bannerText.innerHTML = html;
      }

      this.#updateBannerIcon(milestoneEl);
    }

    #hideBanner() {
      if (this.banner) {
        this.banner.hidden = true;
      }

      if (this.bannerText) {
        this.bannerText.innerHTML = '';
      }

      this.#updateBannerIcon(null);
    }

    #showStaticBanner() {
      if (!this.dataset.staticMessage) {
        this.#hideBanner();
        return;
      }

      if (this.banner) {
        this.banner.hidden = false;
      }

      if (this.bannerText) {
        this.bannerText.textContent = this.dataset.staticMessage;
      }

      this.#updateBannerIcon(null);
    }

    #updateBannerIcon(milestoneEl) {
      if (!this.bannerIcon) return;

      if (!milestoneEl) {
        this.bannerIcon.hidden = true;
        this.bannerIcon.innerHTML = '';
        return;
      }

      const icon = milestoneEl.querySelector('.cart-threshold-bar__icon');
      if (!icon) {
        this.bannerIcon.hidden = true;
        this.bannerIcon.innerHTML = '';
        return;
      }

      this.bannerIcon.hidden = false;
      this.bannerIcon.innerHTML = icon.innerHTML;
    }

    #formatMessage(template, { amount, threshold, reward }) {
      if (!template) return '';

      const amountHtml = `<strong>${FoxTheme.Currency.formatMoney(amount, this.moneyFormat)}</strong>`;
      const thresholdHtml = `<strong>${FoxTheme.Currency.formatMoney(threshold, this.moneyFormat)}</strong>`;
      const rewardHtml = `<strong>${reward}</strong>`;

      return template
        .replace(/\[amount\]/g, amountHtml)
        .replace(/\[threshold\]/g, thresholdHtml)
        .replace(/\[reward\]/g, rewardHtml);
    }
  }

  customElements.define('cart-threshold-bar', CartThresholdBar);
}
